import { Injectable } from '@angular/core';
import { ApplicationService } from '../application/application.service';
import { ModalService } from '../modal/modal.service';
import { UnsupportedUseCasesService } from '../unsupportedUseCases/unsupportedUseCases.service';
import { CoverpathBasePolicy } from '../../../classes/coverpath_subclasses/CoverpathBasePolicy';
import { CoverpathPolicy } from '../../../classes/coverpath_subclasses/CoverpathPolicy';
import { MMDirectPolicy } from '../../../classes/mmdirect_subclasses/MMDirectPolicy';
import { ModalType } from '../../../js/interfaces/CoverpathTypes';
import { UseCaseWithMessage } from '../../classes/UseCaseWithMessage';
import { LifeBridgePolicy } from '../../../classes/lifebridge_subclasses/LifeBridgePolicy';

@Injectable()
export class InformationModalService {
  constructor(private modalService: ModalService, private appService: ApplicationService) {}

  public showMessageIfPresent(prop: string, parsedValue): void {
    const castedPolicy = this.getCastedPrimaryPolicy();
    const existingPolicies = castedPolicy.insured.existingPolicies;
    const simplePropertyName = prop.split('.').slice(-1).join();

    let warningCase = null;
    switch (simplePropertyName) {
      case 'existingPolicyList':
      case 'existingInForcePolicyList':
        if (
          parsedValue >= 0 &&
          existingPolicies[simplePropertyName][parsedValue].cashValueTransfer &&
          existingPolicies[simplePropertyName][parsedValue].cashValueTransfer > 0
        ) {
          warningCase = UseCaseWithMessage.cashValueReplacementRemoved;
        }
        break;
      case 'alirLumpSumUses1035':
      case 'hasAlirRider':
        if (parsedValue === false && existingPolicies.hasCashValueReplacement()) {
          warningCase = UseCaseWithMessage.alir1035ExchangeRemoved;
        }
        break;
      case 'type':
        if (
          prop.includes('existingPolicies') &&
          !['universal', 'variable', 'whole'].includes(parsedValue) &&
          this.currentIterateCollectIsACashValueReplacement(prop, castedPolicy)
        ) {
          warningCase = UseCaseWithMessage.cashValueReplacementRemoved;
        }
        break;
      case 'isReplacement':
      case 'cashValueTransfer':
        if (!parsedValue && this.currentIterateCollectIsACashValueReplacement(prop, castedPolicy)) {
          warningCase = UseCaseWithMessage.cashValueReplacementRemoved;
        }
        break;
      case 'hasExistingInforcePolicies':
        if (
          !parsedValue &&
          existingPolicies.existingInForcePolicyList.some((pol) => pol && pol.cashValueTransfer > 0)
        ) {
          warningCase = UseCaseWithMessage.cashValueReplacementRemoved;
        }
        break;
      case 'replaceInforceApplication':
        if (!parsedValue && existingPolicies.existingPolicyList.some((pol) => pol && pol.cashValueTransfer > 0)) {
          warningCase = UseCaseWithMessage.cashValueReplacementRemoved;
        }
        break;
      case 'alir1035ExchangeAmount':
        if (parsedValue && !existingPolicies.hasCashValueReplacement()) {
          warningCase = UseCaseWithMessage.alir1035ExchangeAmount;
        }
        break;
      case 'retirementReasonForInsurance':
        if (parsedValue === 'Long Term Care') {
          warningCase = UseCaseWithMessage.retirementReasonForInsurance;
        }
        break;
      default:
        break;
    }

    if (prop.includes('hasSigner') && parsedValue === false && castedPolicy.insuredType === 'grandchild') {
      this.modalService.activate({
        type: ModalType.checkLegalGuardian,
        notShowClose: true,
        disableEscapeKeyPress: true,
      });
    }

    if (warningCase !== null) {
      this.activateModal(warningCase);
    }
  }

  private getCastedPrimaryPolicy(): CoverpathPolicy | MMDirectPolicy | LifeBridgePolicy {
    const application = this.appService.getValue('customer.application');
    const primaryPolicy: CoverpathBasePolicy = application.primaryPolicy;
    let castedPolicy: CoverpathPolicy | MMDirectPolicy;
    if (primaryPolicy.isCoverpathPolicy()) {
      castedPolicy = <CoverpathPolicy>primaryPolicy;
    } else if (primaryPolicy.isMMDirectPolicy()) {
      castedPolicy = <MMDirectPolicy>primaryPolicy;
    } else if (primaryPolicy.isLifeBridge()) {
      castedPolicy = <LifeBridgePolicy>primaryPolicy;
    }
    return castedPolicy;
  }

  private activateModal(useCaseWithMessage: UseCaseWithMessage): void {
    this.modalService.activate({
      type: ModalType.useCaseWithMessage,
      useCaseWithMessage: useCaseWithMessage,
    });
  }

  // Determines if the given prop represents an iterate collect item with a cash value replacement
  private currentIterateCollectIsACashValueReplacement(prop: string, castedPolicy: CoverpathBasePolicy): boolean {
    const index = UnsupportedUseCasesService.getLoopIndex(prop);
    if (index >= 0) {
      let existingPolicy;
      if (prop.includes('existingInForcePolicyList')) {
        existingPolicy = castedPolicy.insured.existingPolicies.existingInForcePolicyList[index];
      } else if (prop.includes('existingPolicyList')) {
        existingPolicy = castedPolicy.insured.existingPolicies.existingPolicyList[index];
      }
      return existingPolicy && existingPolicy.isReplacement && existingPolicy.cashValueTransfer > 0;
    }
    return false;
  }
}
